import React, { memo } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import EmployeeSearch from '../../common/EmployeeSearch';
import MeetingTypeFilterSelector from '../../common/filters/MeetingTypeFilterSelector';
import MeetingStatusMultiSelector from '../../common/filters/MeetingStatusMultiSelector';
import MeetingActionItemsFilterSelector from '../../common/filters/MeetingActionItemsFilterSelector';
import MeetingTalkingPointsFilterSelector from '../../common/filters/MeetingTalkingPointsFilterSelector';
import MeetingDatePresetSelector from '../../common/filters/MeetingDatePresetSelector';

/**
 * MeetingFilters - Filter bar for the meetings list view
 */
const MeetingFilters = ({
  // Search
  searchInputValue,
  setSearchInputValue,
  // Date
  datePreset,
  setDatePreset,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  // Participants
  selectedParticipants,
  setSelectedParticipants,
  // Types
  selectedTypes,
  setSelectedTypes,
  // Statuses
  selectedStatuses,
  setSelectedStatuses,
  // Action Items
  actionItemsFilter,
  setActionItemsFilter,
  // Talking Points
  talkingPointsFilter,
  setTalkingPointsFilter,
}) => {
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      height: '40px',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 14px',
    },
  };

  const multiSelectSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: '40px',
      backgroundColor: 'white',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      {/* Row 1: Search, Date Preset, Participants */}
      <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-end' }}>
        {/* Search */}
        <Box sx={{ width: '25%' }}>
          <TextField
            placeholder="Search meetings..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '100%', ...inputSx }}
          />
        </Box>

        {/* Date Preset with Custom Date Pickers */}
        <MeetingDatePresetSelector
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
        />

        {/* Participants */}
        <Box sx={{ flex: 1 }}>
          <EmployeeSearch
            multiple
            value={selectedParticipants}
            onChange={setSelectedParticipants}
            placeholder="Filter by participants..."
            width="100%"
            hideLabel
          />
        </Box>
      </Box>

      {/* Row 2: Meeting Types, Statuses, Action Items, Talking Points */}
      <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-end' }}>
        {/* Meeting Types */}
        <Box sx={{ width: '25%' }}>
          <MeetingTypeFilterSelector
            multiple
            value={selectedTypes}
            onChange={setSelectedTypes}
            label=""
            placeholder="Meeting types..."
            sx={multiSelectSx}
          />
        </Box>

        {/* Meeting Statuses */}
        <Box sx={{ width: '25%' }}>
          <MeetingStatusMultiSelector
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            label=""
            placeholder="Statuses..."
            sx={multiSelectSx}
          />
        </Box>

        {/* Action Items Filter */}
        <Box sx={{ width: '20%' }}>
          <MeetingActionItemsFilterSelector
            value={actionItemsFilter}
            onChange={setActionItemsFilter}
            sx={multiSelectSx}
          />
        </Box>

        {/* Talking Points Filter */}
        <Box sx={{ width: '20%' }}>
          <MeetingTalkingPointsFilterSelector
            value={talkingPointsFilter}
            onChange={setTalkingPointsFilter}
            sx={multiSelectSx}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(MeetingFilters);
